import React, { useCallback, useEffect, useState } from 'react';
import { usePlaidLink } from 'react-plaid-link';
import { apiClient } from '../api/client';
import { Button } from './Button';
import './PlaidLink.css';

interface PlaidLinkProps {
  onSuccess?: () => void;
  onError?: (error: string) => void;
  buttonText?: string;
  className?: string;
  /** Код страны для Plaid (US, CA и т.д.) */
  countryCode?: string;
}

export const PlaidLink: React.FC<PlaidLinkProps> = ({
  onSuccess,
  onError,
  buttonText = 'Привязать банк',
  className = '',
  countryCode = 'US',
}) => {
  const [linkToken, setLinkToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Получаем link token
  useEffect(() => {
    const fetchLinkToken = async () => {
      try {
        setLoading(true);
        const response = await apiClient.createPlaidLinkToken(countryCode);
        setLinkToken(response.link_token);
      } catch (err: any) {
        console.error('Failed to create link token:', err);
        setError(err.message || 'Не удалось создать ссылку для подключения');
        if (onError) {
          onError(err.message || 'Не удалось создать ссылку для подключения');
        }
      } finally {
        setLoading(false);
      }
    };

    fetchLinkToken();
  }, [onError, countryCode]);

  const onSuccessCallback = useCallback(
    async (publicToken: string) => {
      try {
        setLoading(true);
        const result = await apiClient.exchangePlaidPublicToken(publicToken);
        
        console.log('✅ Bank connected successfully:', result);
        
        // Не синхронизируем транзакции автоматически - пользователь может сделать это вручную
        // Синхронизация может вызвать ошибки и замедлить процесс подключения
        
        // Даем небольшую задержку перед вызовом onSuccess для обновления списка
        setTimeout(() => {
          if (onSuccess) {
            onSuccess();
          }
        }, 500);
      } catch (err: any) {
        console.error('Failed to exchange public token:', err);
        const errorMessage = err.message || 'Не удалось подключить банк';
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      } finally {
        setLoading(false);
      }
    },
    [onSuccess, onError]
  );

  const onExitCallback = useCallback(
    (err: any, metadata: any) => {
      if (err) {
        console.error('Plaid Link error:', err);
        const errorMessage = err.error_message || 'Ошибка подключения банка';
        setError(errorMessage);
        if (onError) {
          onError(errorMessage);
        }
      } else if (metadata.status === 'requires_verification') {
        // Пользователь должен верифицировать аккаунт вне приложения
        console.log('Requires verification:', metadata);
      }
    },
    [onError]
  );

  const { open, ready } = usePlaidLink({
    token: linkToken,
    onSuccess: onSuccessCallback,
    onExit: onExitCallback,
  });

  const handleClick = () => {
    if (ready && linkToken) {
      open();
    }
  };

  if (error) {
    return (
      <div className={`plaid-link-error ${className}`}>
        <p className="plaid-link-error__text">{error}</p>
        <Button
          variant="secondary"
          size="small"
          onClick={() => {
            setError(null);
            window.location.reload(); // Перезагружаем для повторной попытки
          }}
        >
          Попробовать снова
        </Button>
      </div>
    );
  }

  return (
    <Button
      variant="primary"
      size="medium"
      onClick={handleClick}
      disabled={!ready || loading || !linkToken}
      loading={loading}
      className={className}
      icon={
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path
            d="M12 2L2 7L12 12L22 7L12 2Z"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2 17L12 22L22 17"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
          <path
            d="M2 12L12 17L22 12"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          />
        </svg>
      }
    >
      {buttonText}
    </Button>
  );
};

